import React, { useEffect, useMemo } from 'react';
import { Form } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useGetPhdStudyFieldsQuery,
  useGetPhdWorkingGroupCandidatesQuery,
  useGetPhdWorkingGroupsQuery,
  useSavePhdWorkingGroupMutation,
} from '../../../../../redux/services/phd';
import { AppBarCMS } from '../../../../basic';
import { AdminContentBlock, Spinner } from '../../../../common';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import {
  normalizeCollection,
  normalizePersonId,
  resolvePersonLabel,
} from '../PhdStudyFields/phdStudyFieldsHelpers.js';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';

const PhdWorkingGroupsEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const fieldId = Number(id);
  const pageTitle = t('Pracovná skupina');
  const { saveSuccess, saveFailed } = useToastMessages();
  const { data: groups, isLoading: isGroupsLoading } =
    useGetPhdWorkingGroupsQuery();
  const { data: studyFields, isLoading: isFieldsLoading } =
    useGetPhdStudyFieldsQuery();
  const { data: externalCandidates, isLoading: isCandidatesLoading } =
    useGetPhdWorkingGroupCandidatesQuery();
  const { personOptions, isLoading: isReferenceLoading } = usePhdReferenceData({
    includeStudents: false,
    includeDepartments: false,
    includeStudyFields: false,
    includeUsers: true,
  });
  const [saveWorkingGroup, { isLoading: isSaving }] =
    useSavePhdWorkingGroupMutation();

  const group = useMemo(
    () =>
      normalizeCollection(groups).find(
        (item) => item.phd_study_field_id === fieldId,
      ),
    [fieldId, groups],
  );
  const field = useMemo(
    () => normalizeCollection(studyFields).find((item) => item.id === fieldId),
    [fieldId, studyFields],
  );
  const { markPristine, modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, group?.id);

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  useEffect(() => {
    form.setFieldsValue({
      chair_person_id: group?.chair_person_id
        ? normalizePersonId(group.chair_person_id)
        : null,
      member_person_ids: (group?.members || []).map((member) =>
        normalizePersonId(member.person_id),
      ),
    });
    markPristine();
  }, [form, group, markPristine]);

  const personSelectOptions = useMemo(() => {
    const optionsById = new Map();

    (personOptions || []).forEach((option) => {
      if (option?.value != null) {
        const value = normalizePersonId(option.value);
        optionsById.set(value, { ...option, value });
      }
    });
    normalizeCollection(externalCandidates).forEach((candidate) => {
      if (candidate?.person_id != null || candidate?.external_user_id != null) {
        const value = candidate.person_id != null
          ? normalizePersonId(candidate.person_id)
          : `external:${candidate.external_user_id}`;
        optionsById.set(value, {
          value,
          label:
            candidate.full_name || candidate.mail || `${value}`,
        });
      }
    });
    [group?.chair, ...(group?.members || [])].forEach((person) => {
      if (person?.person_id != null) {
        const value = normalizePersonId(person.person_id);
        if (!optionsById.has(value)) {
          optionsById.set(value, { value, label: resolvePersonLabel(person) });
        }
      }
    });

    return Array.from(optionsById.values());
  }, [externalCandidates, group, personOptions]);

  const handleSubmit = async (values) => {
    const chairValue = values.chair_person_id;
    const chairIsExternal = String(chairValue ?? '').startsWith('external:');
    const memberValues = values.member_person_ids || [];
    try {
      await saveWorkingGroup({
        id: group?.id,
        body: {
          phd_study_field_id: fieldId,
          chair_person_id: chairIsExternal ? null : chairValue ?? null,
          chair_external_user_id: chairIsExternal
            ? Number(String(chairValue).slice('external:'.length))
            : null,
          member_person_ids: memberValues.filter(
            (value) => !String(value).startsWith('external:'),
          ),
          member_external_user_ids: memberValues
            .filter((value) => String(value).startsWith('external:'))
            .map((value) => Number(String(value).slice('external:'.length))),
        },
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdWorkingGroups));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if (
    isGroupsLoading ||
    isFieldsLoading ||
    isCandidatesLoading ||
    isReferenceLoading ||
    !field
  ) {
    return <Spinner />;
  }

  return (
    <>
      {modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdWorkingGroups}
        backUrlTooltip={t('Pracovné skupiny')}
        permissionsBasic="phd-working-groups"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú pracovné skupiny?')}
          description={t(
            'Skupina posudzuje témy iba svojho odboru. Osoba môže patriť najviac do jednej pracovnej skupiny a predseda nemôže byť zároveň jej členom.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-users"
          link={routes.cmsPhdWorkingGroups}
          title={pageTitle}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
          >
            <FormSelect
              label={
                <span>
                  {t('Odbor')}
                  <PhdHelpHint
                    label={t('Čo znamená odbor pracovnej skupiny?')}
                    title={t(
                      'Určuje témy, ktoré môže pracovná skupina posudzovať. Odbor sa na tejto stránke nemení.',
                    )}
                  />
                </span>
              }
              name="phd_study_field_id"
              options={[{ value: field.id, label: field.study_field }]}
              initialValue={field.id}
              disabled
            />
            <FormSelect
              label={
                <span>
                  {t('Predseda pracovnej skupiny')}
                  <PhdHelpHint
                    label={t('Čo robí predseda pracovnej skupiny?')}
                    title={t(
                      'Predseda vedie posudzovanie tém odboru a nemôže byť zároveň uvedený medzi členmi tej istej skupiny.',
                    )}
                  />
                </span>
              }
              name="chair_person_id"
              options={personSelectOptions}
            />
            <FormSelect
              label={
                <span>
                  {t('Členovia pracovnej skupiny')}
                  <PhdHelpHint
                    label={t('Kto môže byť členom pracovnej skupiny?')}
                    title={t(
                      'Vybraná osoba nesmie patriť do inej pracovnej skupiny. Uložením sa zosynchronizujú príslušné PhD roly.',
                    )}
                  />
                </span>
              }
              name="member_person_ids"
              options={personSelectOptions}
              mode="multiple"
            />
            <FormButtonsContainer>
              <FormButton submitOnClick disabled={isSaving}>
                {t('Uložiť')}
              </FormButton>
            </FormButtonsContainer>
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdWorkingGroupsEdit;
